import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const RATING_OPTIONS = [
  { value: 1, emoji: '😞', label_en: 'Poor', label_ar: 'سيئ', label_fr: 'Mauvais' },
  { value: 2, emoji: '😐', label_en: 'Fair', label_ar: 'مقبول', label_fr: 'Passable' },
  { value: 3, emoji: '😊', label_en: 'Good', label_ar: 'جيد', label_fr: 'Bien' },
  { value: 4, emoji: '😍', label_en: 'Excellent', label_ar: 'ممتاز', label_fr: 'Excellent' },
];

export const RatingPrompt = ({ onSubmit, onClose, triggerType }) => {
  const { t, language } = useLanguage();
  const [selectedRating, setSelectedRating] = useState(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedRating) return;
    
    setIsSubmitting(true);
    
    const ratingOption = RATING_OPTIONS.find(opt => opt.value === selectedRating);
    await onSubmit({
      rating: selectedRating,
      emoji: ratingOption.emoji,
      comment: comment.trim() || null,
    });
    
    setIsSubmitting(false);
  };

  const getMessage = () => {
    if (triggerType === 'before_payment') {
      return language === 'ar' 
        ? 'كيف كانت تجربتك معنا حتى الآن؟' 
        : language === 'fr'
        ? 'Comment s\'est passée votre expérience jusqu\'à présent?'
        : 'How has your experience been so far?';
    }
    if (triggerType === 'farewell') {
      return language === 'ar'
        ? 'شكراً لزيارتك! ما رأيك بالمساعدة المقدمة؟'
        : language === 'fr'
        ? 'Merci de votre visite! Comment avez-vous trouvé notre assistance?'
        : 'Thanks for chatting! How would you rate our assistance?';
    }
    if (triggerType === 'sentiment') {
      return language === 'ar'
        ? 'نقدر رأيك! كيف يمكننا تحسين تجربتك؟'
        : language === 'fr'
        ? 'Votre avis compte! Comment pouvons-nous améliorer?'
        : 'Your feedback matters! How can we improve?';
    }
    return language === 'ar'
      ? 'كيف كانت تجربتك معنا؟'
      : language === 'fr'
      ? 'Comment était votre expérience?'
      : 'How was your experience?';
  };

  const getLabel = (option) => {
    if (language === 'ar') return option.label_ar;
    if (language === 'fr') return option.label_fr;
    return option.label_en;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-lg p-4 mb-3 border-2 border-blue-200 dark:border-blue-700 shadow-lg"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          ⭐ {getMessage()}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close rating"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Rating Buttons */}
      <div className="flex gap-2 mb-3 justify-center">
        {RATING_OPTIONS.map((option) => (
          <motion.button
            key={option.value}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedRating(option.value)}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg transition-all ${
              selectedRating === option.value
                ? 'bg-blue-500 dark:bg-blue-600 shadow-lg ring-2 ring-blue-300 dark:ring-blue-400'
                : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <span className="text-3xl">{option.emoji}</span>
            <span className={`text-xs font-medium ${
              selectedRating === option.value
                ? 'text-white'
                : 'text-gray-600 dark:text-gray-300'
            }`}>
              {getLabel(option)}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Comment Field (optional) */}
      <AnimatePresence>
        {selectedRating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-3"
          >
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                language === 'ar'
                  ? 'أخبرنا المزيد (اختياري)...'
                  : language === 'fr'
                  ? 'Dites-nous en plus (facultatif)...'
                  : 'Tell us more (optional)...'
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 resize-none"
              rows="2"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <motion.button
        whileHover={{ scale: selectedRating ? 1.02 : 1 }}
        whileTap={{ scale: selectedRating ? 0.98 : 1 }}
        onClick={handleSubmit}
        disabled={!selectedRating || isSubmitting}
        className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-all ${
          selectedRating && !isSubmitting
            ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
        }`}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              ⏳
            </motion.span>
            {language === 'ar' ? 'جاري الإرسال...' : language === 'fr' ? 'Envoi...' : 'Sending...'}
          </span>
        ) : (
          <>
            {language === 'ar' ? 'إرسال التقييم' : language === 'fr' ? 'Envoyer' : 'Submit Rating'}
          </>
        )}
      </motion.button>
    </motion.div>
  );
};
